import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Handshake, CheckCircle2, Briefcase, Users, Shield, TrendingUp, Building2, Home } from 'lucide-react';
import { sendViaWeb3Forms } from '../services/web3formsService';
export function PartnerPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries()) as Record<string, string>;
    // Envoyer via Web3Forms (email à contact@adcc.africa)
    const result = await sendViaWeb3Forms({
      type: 'partenariat',
      fields: data
    });
    console.log('📋 Partenariat submission result:', result);
    setIsLoading(false);
    setSubmitted(true);
  };
  if (submitted) {
    return <Layout data-id="element-1597">
        <div className="min-h-[60vh] flex items-center justify-center bg-gray-50 px-4" data-id="element-1598">
          <Card className="max-w-md w-full text-center p-8" data-id="element-1599">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1600">
              <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-1601" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1602">
              Demande reçue !
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-1603">
              Merci de votre intérêt pour un partenariat avec l'ADCC. Nous avons
              bien reçu vos informations et vous avez envoyé un email de
              confirmation. Notre équipe vous recontactera très prochainement
              pour discuter des opportunités de collaboration et de recrutement.
            </p>
            <Button onClick={() => window.location.href = '/'} variant="outline" data-id="element-1604">
              Retour à l'accueil
            </Button>
          </Card>
        </div>
      </Layout>;
  }
  return <Layout data-id="element-1605">
      <SEO {...pageSEO.partner} data-id="element-1606" />
      <div className="bg-[#2C3E50] text-white py-16" data-id="element-1607">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1608">
          <h1 className="text-4xl font-bold mb-4" data-id="element-1609">Devenir Partenaire</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-1610">
            Rejoignez notre réseau d'employeurs partenaires et recrutez des
            personnels qualifiés, formés et accompagnés par l'ADCC dans un cadre
            éthique et transparent.
          </p>
        </div>
      </div>

      {/* Types de Partenaires */}
      <section className="py-16 bg-white" data-id="element-1611">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1612">
          <div className="text-center mb-12" data-id="element-1613">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1614">
              Qui peut devenir partenaire ?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1615">
              L'ADCC collabore avec différents types d'employeurs pour faciliter
              l'insertion professionnelle digne et durable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1616">
            {[{
            icon: Home,
            title: 'Familles',
            desc: 'Recrutez du personnel de maison qualifié pour votre foyer',
            examples: "Aide ménagère, garde d'enfants, cuisinière"
          }, {
            icon: Building2,
            title: 'Entreprises',
            desc: "Trouvez des personnels d'entretien et de services pour vos locaux",
            examples: "Agents d'entretien, maintenance, services généraux"
          }, {
            icon: Users,
            title: 'Institutions',
            desc: "Collaborez pour des programmes d'insertion professionnelle",
            examples: 'ONG, associations, structures publiques'
          }].map((type, index) => <Card key={index} className="text-center border-t-4 border-t-[#1E88E5]" data-id="element-1617">
                <CardContent className="pt-8 pb-6" data-id="element-1618">
                  <div className="inline-flex items-center justify-center p-4 bg-blue-50 rounded-full mb-4" data-id="element-1619">
                    <type.icon className="w-8 h-8 text-[#1E88E5]" data-id="element-1620" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1621">
                    {type.title}
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1622">{type.desc}</p>
                  <p className="text-sm text-gray-500 italic" data-id="element-1623">
                    {type.examples}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Avantages du Partenariat */}
      <section className="py-20 bg-gray-50" data-id="element-1624">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1625">
          <div className="text-center mb-12" data-id="element-1626">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1627">
              Pourquoi Recruter via l'ADCC
            </span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1628">
              Les avantages d'un partenariat avec l'ADCC
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1629">
              Des compétences avérées, des emplois dignes, un accompagnement sur
              mesure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1630">
            {[{
            icon: Briefcase,
            title: 'Personnels Qualifiés et Certifiés',
            desc: 'Accédez à des profils formés professionnellement avec certification reconnue. Compétences techniques et relationnelles validées.'
          }, {
            icon: Shield,
            title: 'Cadre Éthique et Transparent',
            desc: "Recrutement dans le respect des droits, conditions claires, contrats formalisés. Sensibilisation aux bonnes pratiques d'emploi."
          }, {
            icon: Users,
            title: 'Accompagnement Personnalisé',
            desc: 'Matching précis selon vos besoins, préparation du personnel avant placement, intégration facilitée.'
          }, {
            icon: TrendingUp,
            title: 'Suivi Post-Placement',
            desc: 'Accompagnement continu après recrutement, médiation en cas de besoin, garantie de durabilité de la relation professionnelle.'
          }].map((benefit, index) => <Card key={index} className="bg-white" data-id="element-1631">
                <CardContent className="p-6" data-id="element-1632">
                  <div className="flex items-start gap-4" data-id="element-1633">
                    <div className="flex-shrink-0 p-3 bg-blue-50 rounded-lg" data-id="element-1634">
                      <benefit.icon className="w-6 h-6 text-[#1E88E5]" data-id="element-1635" />
                    </div>
                    <div data-id="element-1636">
                      <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1637">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 text-sm" data-id="element-1638">{benefit.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Notre Engagement */}
      <section className="py-20 bg-white" data-id="element-1639">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1640">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1641">
            <div data-id="element-1642">
              <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1643">
                Notre Engagement Éthique Envers Vous
              </span>
              <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-6" data-id="element-1644">
                Une insertion responsable et structurée
              </h2>
              <p className="text-lg text-gray-600 mb-6" data-id="element-1645">
                L'ADCC s'engage à créer un cadre structuré, sécurisé et
                respectueux pour l'emploi des personnels de maison et de
                services.
              </p>

              <div className="space-y-4" data-id="element-1646">
                {['Identification et préparation des profils selon vos besoins', 'Mise en relation transparente avec conditions claires', "Sensibilisation aux bonnes pratiques d'emploi", 'Suivi post-placement et médiation si nécessaire', 'Garantie de respect des droits et de la dignité'].map((item, index) => <div key={index} className="flex items-start gap-3" data-id="element-1647">
                    <CheckCircle2 className="w-6 h-6 text-[#1E88E5] flex-shrink-0 mt-0.5" data-id="element-1648" />
                    <span className="text-gray-700" data-id="element-1649">{item}</span>
                  </div>)}
              </div>
            </div>

            <div className="relative" data-id="element-1650">
              <img src="https://cdn.magicpatterns.com/uploads/jfY9WFQQPLNmCKBmbcpuEW/Femmes_Menage_Emploi_3.png" alt="Personnels formés et certifiés ADCC" className="rounded-2xl shadow-xl w-full" data-id="element-1651" />
              <div className="absolute -bottom-6 -right-6 bg-[#1E88E5] text-white p-6 rounded-lg shadow-lg max-w-xs hidden md:block" data-id="element-1652">
                <p className="font-bold text-lg mb-2" data-id="element-1653">
                  "Notre engagement : transformer les compétences en
                  opportunités d'emploi."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Processus de Partenariat */}
      <section className="py-20 bg-gray-50" data-id="element-1654">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1655">
          <div className="text-center mb-12" data-id="element-1656">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1657">
              Comment ça marche ?
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1658">
              Un processus simple et transparent en 4 étapes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1659">
            {[{
            step: '1',
            title: 'Contactez-nous',
            desc: 'Remplissez le formulaire ci-dessous avec vos besoins'
          }, {
            step: '2',
            title: 'Échange',
            desc: 'Notre équipe vous contacte pour comprendre vos attentes'
          }, {
            step: '3',
            title: 'Matching',
            desc: 'Nous identifions les profils correspondant à vos critères'
          }, {
            step: '4',
            title: 'Placement & Suivi',
            desc: 'Mise en relation, intégration et accompagnement continu'
          }].map((step, index) => <div key={index} className="text-center" data-id="element-1660">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E88E5] text-white rounded-full text-2xl font-bold mb-4" data-id="element-1661">
                  {step.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1662">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1663">{step.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section className="py-20 bg-white" data-id="element-1664">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1665">
          <Card data-id="element-1666">
            <CardContent className="p-8" data-id="element-1667">
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100" data-id="element-1668">
                <div className="p-3 bg-blue-50 rounded-full" data-id="element-1669">
                  <Handshake className="w-8 h-8 text-[#1E88E5]" data-id="element-1670" />
                </div>
                <div data-id="element-1671">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1672">
                    Formulaire de partenariat
                  </h2>
                  <p className="text-gray-500 text-sm" data-id="element-1673">
                    Devenez employeur partenaire et recrutez des personnels
                    qualifiés
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1674">
                <div data-id="element-1675">
                  <label htmlFor="partnerType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1676">
                    Type de partenaire *
                  </label>
                  <select id="partnerType" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border bg-white" data-id="element-1677">
                    <option value="" data-id="element-1678">Sélectionnez...</option>
                    <option value="famille" data-id="element-1679">Famille (particulier)</option>
                    <option value="entreprise" data-id="element-1680">Entreprise</option>
                    <option value="institution" data-id="element-1681">Institution / ONG</option>
                  </select>
                </div>

                <div data-id="element-1682">
                  <label htmlFor="orgName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1683">
                    Nom de l'entreprise / organisation (si applicable)
                  </label>
                  <input type="text" id="orgName" className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border" data-id="element-1684" />
                </div>

                <div data-id="element-1685">
                  <label htmlFor="responsibleName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1686">
                    Nom complet du contact *
                  </label>
                  <input type="text" id="responsibleName" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border" data-id="element-1687" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1688">
                  <div data-id="element-1689">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1690">
                      Email *
                    </label>
                    <input type="email" id="email" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border" data-id="element-1691" />
                  </div>
                  <div data-id="element-1692">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1693">
                      Numéro de téléphone *
                    </label>
                    <input type="tel" id="phone" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border" data-id="element-1694" />
                  </div>
                </div>

                <div data-id="element-1695">
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1696">
                    Adresse / Localisation *
                  </label>
                  <textarea id="address" required rows={2} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border" data-id="element-1697"></textarea>
                </div>

                <div data-id="element-1698">
                  <label htmlFor="needs" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1699">
                    Vos besoins en recrutement *
                  </label>
                  <textarea id="needs" required rows={4} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border" placeholder="Décrivez le type de personnel recherché, les compétences requises, le nombre de postes, etc." data-id="element-1700"></textarea>
                </div>

                <div data-id="element-1701">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1702">
                    Message complémentaire (optionnel)
                  </label>
                  <textarea id="message" rows={3} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border" placeholder="Informations supplémentaires..." data-id="element-1703"></textarea>
                </div>

                <div className="pt-4" data-id="element-1704">
                  <Button type="submit" className="w-full" size="lg" disabled={isLoading} data-id="element-1705">
                    {isLoading ? 'Envoi en cours...' : 'Envoyer la demande de partenariat'}
                  </Button>
                  <p className="text-xs text-gray-500 text-center mt-4" data-id="element-1706">
                    En soumettant ce formulaire, vous acceptez que nous
                    traitions vos données pour vous recontacter et faciliter le
                    processus de recrutement.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>;
}